import { type FC, useCallback } from 'react'

import { type PromotionType } from '@onr/schema'
import { useNavigate } from '@tanstack/react-router'
import { type Except } from 'type-fest'

import { PromotionForm, type PromotionFormProps } from '@/components/organisms/PromotionForm'
import { collections } from '@/schema'

import { fromFormValues } from './fromFormValues'

export interface PromotionCreateFormPageProps extends Except<
  PromotionFormProps,
  'values' | 'disabled' | 'onFormSubmit' | 'onDelete' | 'storagePath' | 'onDirtyChange'
> {
  type: PromotionType
  initiativeId: string
  regionId?: string
  onDirtyChange?: (isDirty: boolean) => void
}

export const PromotionCreateFormPage: FC<PromotionCreateFormPageProps> = ({
  type,
  initiativeId,
  regionId,
  onDirtyChange,
  enabledPlacements,
  ...props
}) => {
  const navigate = useNavigate()

  const savePromotion = useCallback<PromotionFormProps['onFormSubmit']>(
    async (formData) => {
      await collections.promotions.add({
        ...fromFormValues(formData, enabledPlacements),
        type,
        initiativeId,
        createdAt: new Date(),
        ...(regionId ? { regionId } : {}),
      })

      await navigate({ to: '..' })
    },
    [enabledPlacements, initiativeId, regionId, type, navigate]
  )

  return (
    <div className="h-full p-8">
      <PromotionForm
        promotionType={type}
        storagePath={`promotions/${initiativeId}`}
        onFormSubmit={savePromotion}
        onDirtyChange={onDirtyChange}
        enabledPlacements={enabledPlacements}
        defaultValues={{
          partnerName: '',
          title: '',
          targetUrl: '',
          active: true,
          placements: {},
          creativeUrls: {},
        }}
        {...props}
      />
    </div>
  )
}
